import { useState, useRef } from 'react'
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Stack,
} from '@mui/material'
import { CloudUpload as UploadIcon } from '@mui/icons-material'
import { useT } from '@/i18n/useT'

interface ImportDialogProps {
  open: boolean
  onClose: () => void
  onImport: (file: File) => void
}

export function ImportDialog({ open, onClose, onImport }: ImportDialogProps) {
  const { t } = useT()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/csv',
      ]
      const validExtensions = ['.xlsx', '.xls', '.csv']
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        setError(t('Please select a valid Excel (.xlsx, .xls) or CSV file'))
        setSelectedFile(null)
        return
      }

      setSelectedFile(file)
      setError(null)
    }
  }

  const handleImport = () => {
    if (selectedFile) {
      onImport(selectedFile)
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Import from Excel')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {t('Click to select a file')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('Supported formats:')} .xlsx, .xls, .csv
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {selectedFile && (
            <Alert severity="info">
              {t('Selected:')} <strong>{selectedFile.name}</strong> (
              {(selectedFile.size / 1024).toFixed(2)} KB)
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>{t('Warning:')}</strong>{' '}
              {t('Importing will replace all existing data in the database.')}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button onClick={handleImport} variant="contained" disabled={!selectedFile}>
          {t('Import')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
